import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

const D3IdeaWall = ({ nodes, edges, onNodeClick, width = 1200, height = 800, focusNodeId = null }) => {
    const svgRef = useRef();
    const [selectedNode, setSelectedNode] = useState(null);
    const [simulation, setSimulation] = useState(null);

    useEffect(() => {
        if (!nodes || nodes.length === 0) return;

        // 清除之前的內容
        d3.select(svgRef.current).selectAll("*").remove();

        // 創建 SVG
        const svg = d3.select(svgRef.current)
            .attr("width", width)
            .attr("height", height)
            .style("background", "#f8f9fa")
            .style("border-radius", "8px");

        // 創建縮放行為
        const zoom = d3.zoom()
            .scaleExtent([0.1, 4])
            .on("zoom", (event) => {
                container.attr("transform", event.transform);
            });

        svg.call(zoom);

        // 創建主容器
        const container = svg.append("g");

        // 準備數據
        const nodeData = nodes.map(node => ({
            id: node.id,
            title: node.title || '無標題',
            content: node.content || '',
            author: node.author || '未知',
            createdAt: node.createdAt || new Date(),
            nodeType: node.group || 'idea',
            x: node.x || Math.random() * (width - 200) + 100,
            y: node.y || Math.random() * (height - 200) + 100,
            originalNode: node
        }));

        // 創建節點 ID 到節點對象的映射
        const nodeMap = new Map();
        nodeData.forEach(node => {
            nodeMap.set(node.id, node);
        });

        const linkData = edges.map(edge => {
            const sourceNode = nodeMap.get(edge.from);
            const targetNode = nodeMap.get(edge.to);
            
            // 只包含存在的節點連線
            if (sourceNode && targetNode) {
                return {
                    source: sourceNode,
                    target: targetNode,
                    id: `${edge.from}-${edge.to}`
                };
            }
            return null;
        }).filter(link => link !== null); // 過濾掉無效的連線

        // 節點類型顏色映射 - 與 Forum.js 完全一致
        const nodeTypeColors = {
            'idea': '#FFC', // 想法 - 與 Forum.js 一致
            'question': '#CCF', // 提問 - 與 Forum.js 一致
            'experiment': '#FFDBDB', // 實驗 - 與 Forum.js 一致
            'record': '#B9DCF4', // 記錄 - 與 Forum.js 一致
            'information': '#CFC', // 資訊 - 與 Forum.js 一致
            'reply': '#FFF' // 回覆 - 與 Forum.js 一致
        };

        // 節點類型圖標映射
        const nodeTypeIcons = {
            'idea': '💡',
            'question': '❓',
            'experiment': '🧪',
            'record': '📄',
            'information': '🔍',
            'reply': '💬'
        };

        // 創建力導向模擬 - 優化配置減少劇烈移動
        const simulationInstance = d3.forceSimulation(nodeData)
            .force("link", d3.forceLink(linkData)
                .id(d => d.id)
                .distance(150) // 減少連線距離，讓節點更緊密
                .strength(0.8) // 增加連線強度，讓關係更明顯
            )
            .force("charge", d3.forceManyBody()
                .strength(-800) // 減少排斥力，避免劇烈移動
            )
            .force("center", d3.forceCenter(width / 2, height / 2))
            .force("collision", d3.forceCollide()
                .radius(100) // 減少碰撞半徑
            )
            .alphaDecay(0.05) // 增加衰減率，讓模擬更快穩定
            .velocityDecay(0.8); // 增加速度衰減，減少震盪

        // 保存模擬實例
        setSimulation(simulationInstance);

        // 創建連線 - 調整箭頭樣式
        const links = container.append("g")
            .attr("class", "links")
            .selectAll("line")
            .data(linkData)
            .enter().append("line")
            .attr("stroke", "#666666") // 適中的顏色
            .attr("stroke-width", 2) // 適中的線條
            .attr("stroke-opacity", 0.7) // 適中的透明度
            .attr("marker-end", "url(#arrowhead)");

        // 添加箭頭標記 - 適中的箭頭大小
        svg.append("defs").append("marker")
            .attr("id", "arrowhead")
            .attr("viewBox", "0 -5 10 10")
            .attr("refX", 8) // 調整箭頭位置，指向邊緣
            .attr("refY", 0)
            .attr("markerWidth", 6) // 適中的箭頭大小
            .attr("markerHeight", 6)
            .attr("orient", "auto")
            .append("path")
            .attr("d", "M0,-5L10,0L0,5")
            .attr("fill", "#666666") // 與連線顏色一致
            .attr("stroke", "#666666")
            .attr("stroke-width", 1);

        // 創建節點組
        const nodeGroups = container.append("g")
            .attr("class", "nodes")
            .selectAll("g")
            .data(nodeData)
            .enter().append("g")
            .attr("class", "node")
            .attr("data-node-id", d => d.id) // 添加節點 ID 屬性
            .style("cursor", "pointer")
            .call(d3.drag()
                .on("start", dragstarted)
                .on("drag", dragged)
                .on("end", dragended)
            )
            .on("click", (event, d) => {
                // 停止物理模擬，避免劇烈移動
                simulationInstance.alphaTarget(0).restart();
                setSelectedNode(d);
                if (onNodeClick) {
                    onNodeClick(d.id);
                }
            });

        // 節點背景 - 改為正方形，移除圓角
        nodeGroups.append("rect")
            .attr("width", 120) // 正方形寬度
            .attr("height", 120) // 正方形高度
            .attr("rx", 0) // 移除圓角
            .attr("ry", 0) // 移除圓角
            .attr("x", -60) // 調整位置
            .attr("y", -60) // 調整位置
            .style("fill", d => nodeTypeColors[d.nodeType] || '#CCCCCC')
            .style("stroke", d => {
                // 根據節點類型設置邊框顏色
                const borderColors = {
                    'idea': '#FFC',
                    'question': '#CCF',
                    'experiment': '#FFDBDB',
                    'record': '#B9DCF4',
                    'information': '#CFC',
                    'reply': '#FFF'
                };
                return borderColors[d.nodeType] || '#CCCCCC';
            })
            .style("stroke-width", 2)
            .style("filter", "drop-shadow(0 4px 8px rgba(0,0,0,0.1))");

        // 節點圖標 - 調整大小
        nodeGroups.append("text")
            .attr("text-anchor", "middle")
            .attr("dy", -35) // 調整位置
            .style("font-size", "20px") // 適中的圖標大小
            .text(d => nodeTypeIcons[d.nodeType] || '📝');

        // 節點標題 - 增大字體
        nodeGroups.append("text")
            .attr("text-anchor", "middle")
            .attr("dy", -5) // 調整位置
            .style("font-size", "16px") // 增大標題字體
            .style("font-weight", "bold")
            .style("fill", "#333")
            .style("pointer-events", "none")
            .text(d => {
                const title = d.title || '無標題';
                return title.length > 12 ? title.substring(0, 12) + '...' : title; // 調整標題長度
            });

        // 節點作者 - 增大字體
        nodeGroups.append("text")
            .attr("text-anchor", "middle")
            .attr("dy", 15) // 調整位置
            .style("font-size", "12px") // 增大作者字體
            .style("fill", "#666")
            .style("pointer-events", "none")
            .text(d => d.author);

        // 節點時間 - 增大字體
        nodeGroups.append("text")
            .attr("text-anchor", "middle")
            .attr("dy", 35) // 調整位置
            .style("font-size", "11px") // 增大時間字體
            .style("fill", "#999")
            .style("pointer-events", "none")
            .text(d => {
                const date = new Date(d.createdAt);
                return date.toLocaleDateString('zh-TW', { 
                    month: '2-digit', 
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            });

        // 拖曳函數 - 優化拖曳體驗
        function dragstarted(event, d) {
            // 減少拖曳時的物理模擬強度
            if (!event.active) simulationInstance.alphaTarget(0.1).restart();
            d.fx = d.x;
            d.fy = d.y;
        }

        function dragged(event, d) {
            d.fx = event.x;
            d.fy = event.y;
        }

        function dragended(event, d) {
            // 拖曳結束時停止物理模擬
            if (!event.active) simulationInstance.alphaTarget(0);
            d.fx = null;
            d.fy = null;
        }

        // 更新位置
        simulationInstance.on("tick", () => {
            links
                .attr("x1", d => d.source.x)
                .attr("y1", d => d.source.y)
                .attr("x2", d => d.target.x)
                .attr("y2", d => d.target.y);

            nodeGroups
                .attr("transform", d => `translate(${d.x},${d.y})`);
        });

        // 添加節點懸停效果
        nodeGroups
            .on("mouseover", function(event, d) {
                d3.select(this).select("rect")
                    .style("stroke-width", 4)
                    .style("stroke", "#FF6B6B");
            })
            .on("mouseout", function(event, d) {
                d3.select(this).select("rect")
                    .style("stroke-width", 2)
                    .style("stroke", nodeTypeColors[d.nodeType] || '#CCCCCC');
            });

        // 清理函數
        return () => {
            simulationInstance.stop();
        };

    }, [nodes, edges, width, height, onNodeClick]);

    // 聚焦到特定節點
    useEffect(() => {
        if (focusNodeId && simulation && svgRef.current) {
            const svg = d3.select(svgRef.current);
            const nodeGroup = svg.select(`g[data-node-id="${focusNodeId}"]`);
            
            if (nodeGroup.size() > 0) {
                const nodeData = nodeGroup.datum();
                if (nodeData) {
                    // 計算聚焦位置
                    const scale = 2;
                    const translateX = width / 2 - nodeData.x * scale;
                    const translateY = height / 2 - nodeData.y * scale;
                    
                    // 應用縮放和平移
                    svg.select('.nodes')
                        .transition()
                        .duration(750)
                        .attr('transform', `translate(${translateX}, ${translateY}) scale(${scale})`);
                    
                    // 高亮選中的節點
                    nodeGroup.select('rect')
                        .style('stroke-width', 4)
                        .style('stroke', '#FF6B6B');
                    
                    // 3秒後恢復正常樣式
                    setTimeout(() => {
                        nodeGroup.select('rect')
                            .style('stroke-width', 2)
                            .style('stroke', d => {
                                const borderColors = {
                                    'idea': '#FFC',
                                    'question': '#CCF',
                                    'experiment': '#FFDBDB',
                                    'record': '#B9DCF4',
                                    'information': '#CFC',
                                    'reply': '#FFF'
                                };
                                return borderColors[d.nodeType] || '#CCCCCC';
                            });
                    }, 3000);
                }
            }
        }
    }, [focusNodeId, simulation, width, height]);

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />
            {selectedNode && (
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: 'white',
                    padding: '10px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    maxWidth: '300px',
                    fontSize: '12px'
                }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                        {selectedNode.title}
                    </div>
                    <div style={{ color: '#666', marginBottom: '5px' }}>
                        作者：{selectedNode.author}
                    </div>
                    <div style={{ color: '#999', fontSize: '10px' }}>
                        {new Date(selectedNode.createdAt).toLocaleString('zh-TW')}
                    </div>
                </div>
            )}
        </div>
    );
};

export default D3IdeaWall;
